// PROJO federation service — read-only access to projo-dev from MOZAIC web.
// Queries api.personnel, api.assignments, api.project_positions,
// api.organizations, api.employee_certifications, api.certification_types
// to populate the eDSR Workforce section and certification validation.
//
// Why a second Supabase client: PROJO lives in a different Supabase project.
// The PROJO anon key authenticates read-only queries under PROJO RLS. We
// never write to PROJO from MOZAIC.

import { createClient } from '@supabase/supabase-js';
import { differenceInCalendarMonths } from 'date-fns';
import { supabase } from './supabase';

const PROJO_URL = import.meta.env.VITE_PROJO_SUPABASE_URL;
const PROJO_ANON_KEY = import.meta.env.VITE_PROJO_SUPABASE_ANON_KEY;

// Production builds MUST route through the MOZAIC Edge Function — the
// PROJO anon key is not present, and projo-iss RLS rejects it anyway.
// Development builds use the direct client against projo-dev for faster
// iteration (KI-025).
const USE_EDGE_FUNCTION = import.meta.env.VITE_ENV === 'production';

export const isProjoConfigured =
  USE_EDGE_FUNCTION || Boolean(PROJO_URL && PROJO_ANON_KEY);

export const projoClient = PROJO_URL && PROJO_ANON_KEY
  ? createClient(PROJO_URL, PROJO_ANON_KEY, {
      db: { schema: 'api' },
      auth: { persistSession: false, autoRefreshToken: false },
    })
  : null;

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ProjoPersonnel {
  personnel_id: string;           // normalized from worker_id where needed
  first_name: string;
  last_name: string;
  worker_type: string;            // 'W-2' | '1099'
  employer_name: string | null;   // organizations.name via created_by_org_id
  position_name: string | null;   // project_positions.name via assignment.position_id
  hire_date: string | null;
  assignment_start_date: string | null;
  assignment_end_date: string | null;
  assignment_status: string;
  is_sse: boolean;
}

export interface ProjoCertification {
  certification_id: string;
  certification_type_name: string;
  issue_date: string;
  expiry_date: string;
  status: string;                 // active | expiring_soon | expired | revoked
  is_expired: boolean;
}

// ─── SSE rule ───────────────────────────────────────────────────────────────

// Integration map §2: < 6 calendar months in current role qualifies as SSE.
// Uses assignment_start_date when present, falls back to hire_date.
//
// Why differenceInCalendarMonths, not ms-arithmetic: the 180-day
// approximation drifted across month boundaries and false-flagged
// workers past 6 months as SSE (KI-024, Broussard 2025-11-15 on
// 2026-04-14). Calendar-month delta matches how operators think
// about "months in role".
export function calculateSSEStatus(
  hireDate: string | null,
  assignmentStartDate: string | null,
): boolean {
  const anchor = assignmentStartDate ?? hireDate;
  if (!anchor) return false;
  const anchorDate = new Date(anchor);
  if (Number.isNaN(anchorDate.getTime())) return false;
  return differenceInCalendarMonths(new Date(), anchorDate) < 6;
}

// ─── Project ID lookup ──────────────────────────────────────────────────────

export async function getProjoProjectIdForDSR(dsrId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('dsr_reports')
    .select('project_id, projects!inner(projo_project_id)')
    .eq('id', dsrId)
    .maybeSingle();
  if (error) throw error;
  const projects = (data as unknown as { projects: { projo_project_id: string | null } | null })?.projects;
  return projects?.projo_project_id ?? null;
}

// ─── Personnel for project ──────────────────────────────────────────────────

// Calls the MOZAIC Edge Function `projo-personnel`, which proxies the
// PROJO query with a server-side service role key (KI-025). The MOZAIC
// user's JWT is forwarded automatically by supabase-js.
export async function getProjoPersonnelViaEdgeFunction(
  projoProjectId: string,
  reportDate?: string,
): Promise<ProjoPersonnel[]> {
  const onDate = reportDate ?? new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase.functions.invoke<{
    personnel: Omit<ProjoPersonnel, 'is_sse'>[];
  }>('projo-personnel', {
    body: { projectId: projoProjectId, reportDate: onDate },
  });
  if (error) throw error;
  const personnel = data?.personnel ?? [];
  return personnel.map((p) => ({
    ...p,
    is_sse: calculateSSEStatus(p.hire_date, p.assignment_start_date),
  }));
}

// Returns active crew for a PROJO project. Filters to assignments that are
// active/assigned/temporary_leave and whose date window contains the
// report_date (defaults to today). Routes through the Edge Function in
// production, falls back to the direct client in development.
export async function getProjoPersonnelForProject(
  projoProjectId: string,
  reportDate?: string,
): Promise<ProjoPersonnel[]> {
  if (USE_EDGE_FUNCTION) {
    return getProjoPersonnelViaEdgeFunction(projoProjectId, reportDate);
  }
  if (!projoClient) {
    throw new Error('PROJO is not configured. Set VITE_PROJO_SUPABASE_URL and VITE_PROJO_SUPABASE_ANON_KEY.');
  }
  const onDate = reportDate ?? new Date().toISOString().slice(0, 10);

  const { data, error } = await projoClient
    .from('assignments')
    .select(`
      id,
      status,
      assignment_start_date,
      assignment_end_date,
      worker_id,
      position:project_positions!position_id ( name ),
      worker:personnel!worker_id (
        id,
        first_name,
        last_name,
        worker_type,
        hire_date,
        created_by_org_id,
        employer:organizations!created_by_org_id ( name )
      )
    `)
    .eq('project_id', projoProjectId)
    .in('status', ['assigned', 'active', 'temporary_leave'])
    .lte('assignment_start_date', onDate);

  if (error) throw error;

  type Row = {
    id: string;
    status: string;
    assignment_start_date: string | null;
    assignment_end_date: string | null;
    worker_id: string;
    position: { name: string } | null;
    worker: {
      id: string;
      first_name: string;
      last_name: string;
      worker_type: string;
      hire_date: string | null;
      employer: { name: string } | null;
    } | null;
  };

  const rows = (data ?? []) as unknown as Row[];

  return rows
    .filter(r => !r.assignment_end_date || r.assignment_end_date >= onDate)
    .filter(r => r.worker)
    .map(r => ({
      personnel_id: r.worker!.id,
      first_name: r.worker!.first_name,
      last_name: r.worker!.last_name,
      worker_type: r.worker!.worker_type,
      employer_name: r.worker!.employer?.name ?? null,
      position_name: r.position?.name ?? null,
      hire_date: r.worker!.hire_date,
      assignment_start_date: r.assignment_start_date,
      assignment_end_date: r.assignment_end_date,
      assignment_status: r.status,
      is_sse: calculateSSEStatus(r.worker!.hire_date, r.assignment_start_date),
    }));
}

// ─── Active certifications for a person ─────────────────────────────────────

export async function getProjoActiveCertifications(
  personnelId: string,
): Promise<ProjoCertification[]> {
  if (!projoClient) {
    throw new Error('PROJO is not configured.');
  }
  const { data, error } = await projoClient
    .from('employee_certifications')
    .select(`
      id,
      issue_date,
      expiration_date,
      status,
      type:certification_types!certification_type_id ( name )
    `)
    .eq('worker_id', personnelId)
    .in('status', ['active', 'expiring_soon']);

  if (error) throw error;

  type Row = {
    id: string;
    issue_date: string;
    expiration_date: string;
    status: string;
    type: { name: string } | null;
  };

  const today = new Date().toISOString().slice(0, 10);
  return ((data ?? []) as unknown as Row[]).map(r => ({
    certification_id: r.id,
    certification_type_name: r.type?.name ?? 'Unknown',
    issue_date: r.issue_date,
    expiry_date: r.expiration_date,
    status: r.status,
    is_expired: r.expiration_date < today,
  }));
}
